import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatApiError } from '../../utils/formatters';
import SqlSourceView from './SqlSourceView';
import { QueryViewButton, QueryViewDialog } from './QueryView';
import axios from 'axios';
import { FiRefreshCw, FiSearch, FiAlertTriangle, FiInfo, FiZapOff, FiUserX, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useConfirm } from '../ConfirmDialog';
import './SessionAdminModal.css';
import ConnTabsWindow, { ConnTabPane } from './ConnTabsWindow';
import memoWindow from '../common/memoWindow';
import OwnConnTarget from './OwnConnTarget';
import useOwnConnSession, { ORACLE_CONN } from '../../hooks/useOwnConnSession';
import AshTimeline from './AshTimeline';
import { filterSessions, sortSessions, sessionInstances, SESSION_COLUMNS } from '../../utils/sessionList';
import { nextSort } from '../../utils/catalogQuery';
import { pinnedLeftOffsets } from '../../utils/gridPin';
import { requestPerfHistory } from '../../utils/perfJump';

import { menuPoint } from '../../utils/appZoom';
const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const SESS_API = `${API_ROOT}/sql-developer/monitoring/session-admin`;

// 조회 대상 뷰별 탭. 타임라인은 ASH 를 그림으로 본 것이라 같은 자료다.
// 세션 목록의 머리글 — 열 정의(utils/sessionList)의 key 마다. 모듈 스코프 상수에 t() 를 쓰지 않는다.
const sessColLabel = (key, t) => ({
    sid: 'SID,SERIAL#',
    user: t('monitoring:sa.col.user'),
    status: t('monitoring:sa.col.status'),
    machine: t('monitoring:sa.col.machine'),
    program: t('monitoring:sa.col.program'),
    event: t('monitoring:sa.col.waitEvent'),
    elapsed: t('monitoring:sa.col.elapsed'),
    sqlId: 'SQL_ID',
    blocking: t('monitoring:sa.col.blockingSid'),
    module: t('monitoring:sa.col.module'),
    osuser: t('monitoring:sa.col.osuser'),
    action: t('monitoring:sa.col.action'),
    service: t('monitoring:sa.col.service'),
    waitClass: t('monitoring:sa.col.waitClass'),
    secondsInWait: t('monitoring:sa.col.secondsInWait'),
    sqlExecStart: t('monitoring:sa.col.sqlExecStart'),
    prevSqlId: 'PREV_SQL_ID',
    logonTime: t('monitoring:sa.col.logonTime'),
}[key] || key);
const MONO_COLS = new Set(['sid', 'machine', 'event', 'sqlId', 'prevSqlId', 'sqlExecStart', 'logonTime']);

const DETAIL_TABS = (t) => [
    { key: 'timeline', label: t('monitoring:sa.tab.timeline') },
    { key: 'cursors', label: t('monitoring:sa.tab.cursors') },
    { key: 'transaction', label: t('monitoring:sa.tab.transaction') },
    { key: 'stats', label: t('monitoring:sa.tab.stats') },
    { key: 'ash', label: t('monitoring:sa.tab.ash') },
];

const getStatusOptions = (t) => [
    { value: '', label: t('monitoring:sa.allStates') },
    { value: 'ACTIVE', label: 'ACTIVE' },
    { value: 'INACTIVE', label: 'INACTIVE' },
];

// 세션 한 행의 키 = (인스턴스, sid, serial#). sid 는 노드 안에서만 유일하고(RAC 2노드면 같은 sid 가
// 둘), serial# 이 바뀌면 다른 세션이다 — 하나라도 빼면 React 키가 겹치고 「고른 행」 표시가 엉뚱한 행에 붙는다.
/** 세션 풀로 묶을 수 있는가 — 계정·머신·프로그램이 다 있어야 한다(백그라운드 세션은 계정이 없다) */
const canPool = (s) => !!(s && s.username && s.machine && s.program);

/** 타임라인 표본 범위(분) — 서버 상한 240 */
const ASH_MINUTES = [15, 30, 60, 120, 240];

const sessionRowKey = (s) => `${s.instId ?? 1}-${s.sid}-${s.serial}`;

function fmtElapsed(sec, t) {
    if (sec == null) return '-';
    if (sec < 60) return t('monitoring:sa.dur.sec', { n: sec });
    if (sec < 3600) return t('monitoring:sa.dur.min', { n: Math.floor(sec / 60) });
    return t('monitoring:sa.dur.hourMin', { h: Math.floor(sec / 3600), m: Math.floor((sec % 3600) / 60) });
}

/** 연결 하나의 본문 — 창(아래 SessionAdminModal)이 DB 연결마다 탭으로 하나씩 띄운다(ConnTabsWindow, 2026-10-14) */
const SessionAdminModalBody = ({ isOpen: windowOpen = false, onClose, connections = [], darkMode = false, defaultConnectionId, active = true, excludeConnIds = [], onConnectionChange }) => {
    const { t } = useTranslation();
    const confirm = useConfirm();

    // 이 창의 연결·세션은 `useOwnConnSession` 이 쥔다 — 창을 열면 현재 연결에 세션을
    // 하나 만들고, 창을 닫으면 닫는다. 조회는 전부 `run()` 으로 줄을 세운다
    // (한 세션은 한 번에 한 문장이라, 동시에 보내면 서버가 409 로 거절한다).
    const mon = useOwnConnSession({
        isOpen: windowOpen, connections, defaultConnectionId, label: 'session-admin',
        filter: ORACLE_CONN,
    });
    // 연결이 정해지기 전에는 조회하지 않는다(2026-10-14) — 연결 없이 나간 첫 조회에 서버가 예시 데이터를 주어,
    // 창을 열면 예시가 먼저 그려지고 실제 값이 그 위를 덮었다. 고를 연결이 아예 없을 때만 그대로 연다(예시 켜기·오류 안내가 보이게)
    const isOpen = windowOpen && (!!mon.connId || mon.conns.length === 0);
    // 탭 이름이 이 연결을 따라가게 창에 알린다(탭 안에서 연결을 바꿀 수 있다)
    useEffect(() => { if (mon.connId && onConnectionChange) onConnectionChange(mon.connId); }, [mon.connId]); // eslint-disable-line react-hooks/exhaustive-deps
    const selectedConnId = mon.connId;
    // 다른 신규 모니터링 화면과 동일한 방침 — 항상 예시 데이터로 시작, 실접속 실패 시 자동 전환.
    // 예시 데이터는 **꺼진 채로** 시작한다(요청 2026-10-02) — 실제 연결을 보러 연 창이다. 조회가 실패하면 켠다
    const [useDemo, setUseDemo] = useState(false);
    // 창을 열 때마다 예시는 꺼진 상태로 — 앞에서 켠 것이 남아 재현되지 않게(2026-10-03). 저장하지도 않는다
    useEffect(() => { if (isOpen) setUseDemo(false); }, [isOpen]);
    const [backendError, setBackendError] = useState(null);

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDemoData, setIsDemoData] = useState(false);
    const [includeBackground, setIncludeBackground] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    // 목록 보기 — 인스턴스 리본 · 머리글 정렬 · 열 고정 (2026-10-01)
    const [instFilter, setInstFilter] = useState(null);
    const [sort, setSort] = useState(null);             // { col, dir } — utils/catalogQuery 의 nextSort 로 돈다
    const [pinnedCols, setPinnedCols] = useState([]);
    const [colMenu, setColMenu] = useState(null);       // { x, y, key }
    const colMenuRef = useRef(null);
    const thRefs = useRef({});
    const [pinLeft, setPinLeft] = useState({});

    // 목록에서 고른 세션 — 아래 상세 탭과 타임라인이 이것을 본다.
    // **세션 키는 (instId, sid) 다.** RAC 에서 sid 는 노드 안에서만 유일해서,
    // sid 만으로 조회하면 다른 노드의 같은 번호 세션을 집는다.
    const [picked, setPicked] = useState(null);
    // 고른 세션이 목록 새로고침에서 사라졌다(세션 종료) — { instId, sid, serial }. 그 자리에서 짧게 말한다(2026-11-02 신고:
    // 끝난 세션의 타임라인·ASH·커서가 남아 있어 아직 살아 있는 세션처럼 읽혔다)
    const [pickedGone, setPickedGone] = useState(null);
    const pickedRef = useRef(null);
    useEffect(() => { pickedRef.current = picked; }, [picked]);
    const [detailTab, setDetailTab] = useState('timeline'); // timeline|cursors|transaction|stats|ash
    const [detail, setDetail] = useState({});               // { tab: rows }
    const [detailLoading, setDetailLoading] = useState(null);
    const [detailError, setDetailError] = useState(null);
    const [detailIsDemo, setDetailIsDemo] = useState(false);
    const [picked2, setPicked2] = useState(null);           // 타임라인에서 클릭한 구간
    // GV$SQLAREA — 타임라인에 보이는 SQL 들의 **전역 누적** 통계.
    // "한 번이 느린가, 여러 번 돌았나" 에 답한다. 이 세션·이 호출의 값이 아니라는 것은
    // 화면이 말해야 한다(아래 라벨).
    const [sqlArea, setSqlArea] = useState({});
    const [queryViewKind, setQueryViewKind] = useState(null);
    const [queryViewSql, setQueryViewSql] = useState(null);
    // 타임라인의 표본 범위(분) — 배치처럼 오래 도는 세션은 15분으로 모자란다(2026-10-22). 서버 상한은 240
    const [ashMinutes, setAshMinutes] = useState(() => {
        try { const v = Number(localStorage.getItem('livesql_sessadm_ash_min')); return ASH_MINUTES.includes(v) ? v : 15; } catch (_e) { return 15; }
    });
    useEffect(() => { try { localStorage.setItem('livesql_sessadm_ash_min', String(ashMinutes)); } catch (_e) { /* 기억 못 해도 동작한다 */ } }, [ashMinutes]);
    // 세션 풀로 보기(2026-10-22) — 연결 풀을 쓰는 앱(배치)은 일이 풀의 여러 세션에 나뉘어 돈다. 같은 계정·머신·프로그램의 세션을 함께 본다
    const [poolView, setPoolView] = useState(() => {
        try { return localStorage.getItem('livesql_sessadm_pool') === '1'; } catch (_e) { return false; }
    });
    useEffect(() => { try { localStorage.setItem('livesql_sessadm_pool', poolView ? '1' : '0'); } catch (_e) { /* 기억 못 해도 동작한다 */ } }, [poolView]);
    // 타임라인 응답의 덧붙은 값 — { pool, truncated, commitStats }
    const [ashMeta, setAshMeta] = useState({});
    // SQL 전체 원문 — 커서·타임라인의 SQL 을 누르면 아래에 펼친다 { sqlId, loading, text, error, isDemo }
    const [sqlText, setSqlText] = useState(null);

    const [killingKey, setKillingKey] = useState(null); // `${sid}-${serial}` 진행 중 표시
    const [killError, setKillError] = useState(null);

    const selectedConn = mon.conns.find(c => c.id === selectedConnId);
    // 강제 종료를 할 수 있는지는 **DB 계정 권한**(ALTER SYSTEM)이 정한다 — 화면은 막지 않고, 권한이 없으면
    // 서버가 DB 의 거절을 번역해 돌려준다(KILL_NO_PRIVILEGE). 예시 데이터에서는 죽일 세션이 없다.
    const canKill = !!selectedConn && !useDemo;

    // 데모일 때는 세션을 싣지 않는다 — 백엔드가 DB 에 가지 않으므로 세션이 필요 없다.
    // `mon` 을 통째로 의존성에 넣지 말 것 — 폴링으로 session 이 갱신될 때마다
    // 모든 조회가 다시 돈다. 고정된 것(run)과 값(sessionId·creating)만 꺼낸다.
    const { run, creating: monCreating } = mon;

    const target = useCallback((extra) => (useDemo
        ? { demo: 1, ...extra }
        : { ...mon.params, ...extra }), [useDemo, mon.params]);

    // 상세 넷은 같은 모양이라 한 함수로 받는다. 타임라인은 ASH 를 쓰므로 같은 경로다.
    // 늦게 온 응답이 다른 세션의 상세를 덮지 않게 — 마지막 요청만 받는다
    const detailReqRef = useRef(0);
    const fetchDetail = useCallback(async (tab, sess) => {
        if (!sess || monCreating) return;
        const kind = tab === 'timeline' ? 'ash' : tab;
        const reqId = ++detailReqRef.current;
        setDetailLoading(tab);
        setDetailError(null);
        try {
            const res = await run(() => axios.get(`${SESS_API}/${kind}`, {
                params: target({
                    instId: sess.instId ?? 1,
                    sid: sess.sid,
                    // **세션은 (inst, sid, serial) 이다.** SID 만 보내면 그 SID 를 거쳐 간 다른 세션의
                    // ASH 가 섞인다(실측: 병렬 워커 하나에 SERIAL# 7개가 7줄로 떴다).
                    serial: sess.serial ?? undefined,
                    minutes: kind === 'ash' ? ashMinutes : undefined,
                    ...(kind === 'ash' && poolView && canPool(sess)
                        ? { scope: 'pool', username: sess.username, machine: sess.machine, program: sess.program } : {}),
                }),
            }));
            if (reqId !== detailReqRef.current) return;
            setDetail(prev => ({ ...prev, [tab]: res.data?.data || [] }));
            if (res.data?.sqlArea) setSqlArea(res.data.sqlArea);
            if (kind === 'ash') setAshMeta({ pool: !!res.data?.pool, truncated: !!res.data?.truncated, commitStats: res.data?.commitStats || null });
            setDetailIsDemo(res.data?.isDemo !== false);
        } catch (err) {
            if (reqId !== detailReqRef.current) return;
            setDetailError(formatApiError(err, t));
            setDetail(prev => ({ ...prev, [tab]: [] }));
        } finally {
            if (reqId === detailReqRef.current) setDetailLoading(null);
        }
        // target 이 연결·세션·데모를 품고 있다 — 또 나열하면 같은 말을 두 번 한다.
    }, [run, target, monCreating, t, ashMinutes, poolView]);

    // 세션을 고르거나 탭을 바꾸면 그때 받아 온다 — 네 가지를 한꺼번에 읽으면
    // 보지도 않을 조회로 대상 DB 에 부담을 준다.
    // 다른 세션을 고르면 앞 세션의 상세를 먼저 비운다 — 안 비우면 새 조회가 끝날 때까지 앞 세션의 표가
    // 보이다가 바뀌어 「무언가 번쩍 보였다 사라진다」(신고 2026-10-02, 트랜잭션 탭)
    const pickedKey = picked ? sessionRowKey(picked) : null;
    // 불러오는 중이고 아직 받은 것이 없으면 표 대신 「불러오는 중」
    useEffect(() => { setDetail({}); setDetailError(null); setSqlText(null); }, [pickedKey]);

    // SQL 전체 원문 — 같은 것을 다시 누르면 접는다. 공유 풀에서 내려간 SQL 은 null(화면이 그렇게 말한다)
    const sqlTextReq = useRef(0);
    const openSqlText = useCallback(async (sqlId) => {
        if (!sqlId) return;
        if (sqlText && sqlText.sqlId === sqlId && !sqlText.loading) { setSqlText(null); return; }
        const req = ++sqlTextReq.current;
        setSqlText({ sqlId, loading: true });
        try {
            const res = await run(() => axios.get(`${SESS_API}/sql-text`, { params: target({ sqlId }) }));
            if (req !== sqlTextReq.current) return;
            setSqlText({ sqlId, text: res.data?.sqlText ?? null, isDemo: !!res.data?.isDemo });
        } catch (err) {
            if (req !== sqlTextReq.current) return;
            setSqlText({ sqlId, error: formatApiError(err, t) });
        }
    }, [sqlText, run, target, t]);

    // 오라클 성능 분석의 「실행 이력」 으로 — 다른 창으로 넘어가므로 먼저 묻는다(2026-10-22 요청)
    const goHistory = useCallback(async (sqlId) => {
        if (!sqlId || !mon.connId) return;
        const ok = await confirm({
            title: t('monitoring:sa.jump.title'),
            message: t('monitoring:sa.jump.message', { sqlId }),
            confirmText: t('monitoring:sa.jump.confirm'),
        });
        if (ok) requestPerfHistory(mon.connId, sqlId);
    }, [confirm, mon.connId, t]);

    // 상세를 접어 두면 **조회하지 않는다**(요청 2026-10-02) — 목록만 보며 세션을 훑을 때 행마다 대상 DB 에
    // 조회가 나가지 않게. 펴는 순간 고른 세션을 읽는다. 접힘 여부는 이 PC 에 기억한다.
    const [detailOpen, setDetailOpen] = useState(() => {
        try { return localStorage.getItem('livesql_sessadm_detail_open') !== '0'; } catch (_e) { return true; }
    });
    useEffect(() => {
        try { localStorage.setItem('livesql_sessadm_detail_open', detailOpen ? '1' : '0'); } catch (_e) { /* 저장 못 해도 동작한다 */ }
    }, [detailOpen]);

    useEffect(() => {
        if (!isOpen || !picked || !detailOpen) return;
        fetchDetail(detailTab, picked);
    }, [isOpen, picked, detailTab, fetchDetail, detailOpen]);

    // 「쿼리」 — 이 탭이 실제로 돌리는 SQL
    useEffect(() => {
        if (!queryViewKind) { setQueryViewSql(null); return; }
        let alive = true;
        setQueryViewSql(null);
        axios.get(`${SESS_API}/source-sql`, { params: { kind: queryViewKind } })
            .then(r => { if (alive) setQueryViewSql(r.data?.sql || ''); })
            .catch(err => { if (alive) setQueryViewSql({ error: formatApiError(err, t) }); });
        return () => { alive = false; };
    }, [queryViewKind, t]);

    // 고른 세션을 놓는다 — 상세(타임라인·ASH·커서·트랜잭션·통계·SQL 원문)를 모두 비우고, **진행 중인 조회의 응답은
    // 버린다**(요청 번호를 올린다). 안 그러면 늦게 온 응답이 사라진 세션의 내용을 다시 채운다
    const dropPicked = useCallback((gone) => {
        detailReqRef.current += 1;
        sqlTextReq.current += 1;
        pickedRef.current = null;
        setPicked(null);
        setPicked2(null);
        setDetail({});
        setDetailLoading(null);
        setDetailError(null);
        setDetailIsDemo(false);
        setSqlText(null);
        setSqlArea({});
        setAshMeta({});
        setPickedGone(gone || null);
    }, []);

    // 연결·예시 데이터를 바꾸면 고른 세션은 다른 DB 의 것이다 — 「끝났다」 고 말하지 않고 조용히 놓는다
    useEffect(() => { dropPicked(null); }, [selectedConnId, useDemo, dropPicked]);

    // 늦게 온 목록 응답이 새 목록을 덮지 않게 — 마지막 요청만 받는다
    const sessReqRef = useRef(0);
    const fetchSessions = useCallback(async () => {
        if (monCreating) return;
        const reqId = ++sessReqRef.current;
        setLoading(true);
        try {
            const res = await run(() => axios.get(`${SESS_API}/sessions`, {
                params: target({ includeBackground: includeBackground ? 1 : undefined }),
            }));
            if (reqId !== sessReqRef.current) return;
            const list = res.data?.data || [];
            setSessions(list);
            setIsDemoData(res.data?.isDemo !== false);
            setBackendError(null);
            // 고른 세션이 새 목록에 없으면 놓는다 — 키는 (inst, sid, serial#). SID 만 보면 같은 SID 를 이어받은
            // 새 세션을 「아직 있다」 로 읽는다
            const cur = pickedRef.current;
            if (cur && !list.some(s => sessionRowKey(s) === sessionRowKey(cur))) {
                dropPicked({ instId: cur.instId ?? 1, sid: cur.sid, serial: cur.serial });
            }
        } catch (err) {
            if (reqId !== sessReqRef.current) return;
            // 예시 데이터로 **조용히 바꾸지 않는다** — 실패 뒤에 그럴듯한 가짜 세션 목록이 뜨면 실패가 안 보인다
            setBackendError(formatApiError(err, t));
            setSessions([]);
            // 목록을 못 받았으면 고른 세션이 살아 있는지 모른다 — 상세를 남기지 않되 「끝났다」 고 하지도 않는다
            if (pickedRef.current) dropPicked(null);
        } finally {
            if (reqId === sessReqRef.current) setLoading(false);
        }
    }, [run, target, monCreating, includeBackground, t, dropPicked]);

    useEffect(() => {
        if (!isOpen) return;
        fetchSessions();
    }, [isOpen, fetchSessions]);

    // RAC 노드 — 둘 이상일 때만 리본을 세운다. 고른 노드가 목록에서 사라지면 「전체」로 돌아간다
    // (빈 표가 「세션 없음」으로 읽히면 안 된다).
    const instances = useMemo(() => sessionInstances(sessions), [sessions]);
    const activeInst = instances.length > 1 && instances.includes(instFilter) ? instFilter : null;

    // 거르기·정렬은 utils/sessionList 한 곳 — 검색은 모든 열의 보이는 글자를 본다
    const filteredSessions = useMemo(
        () => sortSessions(filterSessions(sessions, { status: statusFilter, inst: activeInst, q: search }), sort),
        [sessions, statusFilter, activeInst, search, sort],
    );
    // 열 순서는 바꾸지 않는다 — 고정한 열은 **제자리에서** 선다(요청 2026-10-02)
    const sessCols = SESSION_COLUMNS;

    // 고정 열의 왼쪽 위치 — 이 표는 폭이 내용에 따라 정해지므로 그려진 머리글 폭을 재서 쌓는다
    // 제자리 고정 — 보이는 순서대로 앞의 고정 열 폭(그려진 머리글을 잰 값)만큼 띄운다(utils/gridPin)
    useLayoutEffect(() => {
        const { left } = pinnedLeftOffsets(sessCols.map(c => c.key), pinnedCols,
            (k) => (thRefs.current[k] && thRefs.current[k].offsetWidth) || 0, 0);
        setPinLeft(prev => (JSON.stringify(prev) === JSON.stringify(left) ? prev : left));
    }, [pinnedCols, sessCols, filteredSessions]);
    const lastPinnedKey = useMemo(() => [...sessCols].reverse().map(c => c.key).find(k => pinnedCols.includes(k)) || null, [sessCols, pinnedCols]);

    // 머리글 우클릭 메뉴 — 바깥을 누르거나 Esc 면 닫는다
    useEffect(() => {
        if (!colMenu) return undefined;
        const onDown = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setColMenu(null); };
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setColMenu(null); } };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey, true);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
    }, [colMenu]);

    // 셀을 누르면 그 값이 검색어가 된다 — 같은 값을 다시 누르면 푼다
    const searchByCell = (text) => {
        if (!text) return;
        setSearch(prev => (prev === text ? '' : text));
    };
    const pickSession = (s) => { setPicked(s); setPicked2(null); setPickedGone(null); };
    const showDetailBody = !(detailLoading === detailTab && (detail[detailTab] || []).length === 0);
    const pinStyle = (key) => (pinLeft[key] != null ? { left: `${pinLeft[key]}px` } : undefined);
    const pinClass = (key) => (pinLeft[key] != null
        ? (key === lastPinnedKey ? ' sessadm-pin sessadm-pin-last' : ' sessadm-pin')
        : '');

    const handleKill = async (session, mode) => {
        if (!canKill) return;
        const ok = await confirm({
            title: t('monitoring:sa.kill'),
            // '즉시' 여부가 문장 중간에 끼어 있어 조각으로 나누면 어순이 깨진다 — 문장을 통째로 둘로 나눈다.
            message: t(mode === 'immediate' ? 'monitoring:sa.killConfirmImmediate' : 'monitoring:sa.killConfirm', {
                sid: session.sid,
                serial: session.serial,
                user: session.username || t('monitoring:sa.unknownUser'),
            }),
            confirmText: t('monitoring:sa.killShort'),
            cancelText: t('monitoring:sa.cancel'),
            variant: 'danger',
        });
        if (!ok) return;

        const key = sessionRowKey(session);
        setKillingKey(key);
        setKillError(null);
        try {
            await run(() => axios.post(`${SESS_API}/kill`, {
                ...mon.params,
                sid: session.sid,
                serial: session.serial,
                mode,
                usrid: 'standalone',
            }));
            await fetchSessions();
        } catch (err) {
            setKillError(formatApiError(err, t));
        } finally {
            setKillingKey(null);
        }
    };

    // 고른 PL/SQL 구간 안에서 각 SQL 이 **표본으로 몇 ms** 차지했나.
    // 이건 이 호출 안의 값이라 GV$SQLAREA 의 전역 누적과 성격이 다르다 — 표에서 갈라 놓는다.
    // 고른 세션이 **병렬 워커**면 그 조정자. 워커 한 줄만으로는 문장 전체가 안 보인다 —
    // 조정자의 타임라인에는 워커가 전부 함께 나온다. 근거는 그 세션 자신의 ASH 행(qc_session_id).
    // (훅이라 아래 `if (!isOpen) return null` 보다 위에 있어야 한다)
    const qcOfPicked = useMemo(() => {
        if (!picked) return null;
        const mine = (detail.timeline || []).filter(r => r.sessionId === picked.sid
            && (r.instId ?? 1) === (picked.instId ?? 1)
            && r.qcSessionId != null && r.qcSessionId !== r.sessionId);
        if (mine.length === 0) return null;
        const last = mine[mine.length - 1];
        return { instId: last.qcInstId ?? picked.instId ?? 1, sid: last.qcSessionId, serial: last.qcSessionSerial ?? null };
    }, [picked, detail.timeline]);

    const goToQc = () => {
        if (!qcOfPicked) return;
        // 목록에 있는 조정자만 고른다 — 목록에 없는(이미 끝난) 세션의 상세는 보이지 않는다(2026-11-02, 위 dropPicked).
        // 끝났으면 고른 것을 놓고 그 사실을 그 자리에서 말한다
        const inList = sessions.find(s => (s.instId ?? 1) === qcOfPicked.instId
            && s.sid === qcOfPicked.sid && (qcOfPicked.serial == null || s.serial === qcOfPicked.serial));
        if (!inList) {
            dropPicked({ instId: qcOfPicked.instId, sid: qcOfPicked.sid, serial: qcOfPicked.serial ?? '?' });
            return;
        }
        setPicked(inList);
        setPicked2(null);
        setPickedGone(null);
        setDetailTab('timeline');
    };

    // SQL 전체 원문 칸 — 커서·타임라인이 함께 쓴다
    const sqlTextPanel = sqlText ? (
        <div className="sessadm-sqltext">
            <div className="sessadm-sqltext-head">
                <span>{t('monitoring:sa.sqlText.title', { sqlId: sqlText.sqlId })}</span>
                {sqlText.isDemo && <span className="sessadm-demo-badge">{t('monitoring:sa.demoData')}</span>}
                <div className="sessadm-spacer" />
                {mon.connId && !useDemo && (
                    <button type="button" className="sessadm-btn sessadm-btn-secondary" onClick={() => goHistory(sqlText.sqlId)}>{t('monitoring:sa.toHistory')}</button>
                )}
                <button type="button" className="sessadm-btn sessadm-btn-secondary" onClick={() => setSqlText(null)} aria-label={t('monitoring:sa.close')} title={t('monitoring:sa.close')}>
                    <FiX size={11} />
                </button>
            </div>
            {sqlText.loading ? (
                <div className="sessadm-empty-note">{t('monitoring:sa.sqlText.loading')}</div>
            ) : sqlText.error ? (
                <div className="sessadm-error"><FiInfo size={13} /> {sqlText.error}</div>
            ) : sqlText.text == null ? (
                <div className="sessadm-empty-note">{t('monitoring:sa.sqlText.gone')}</div>
            ) : (
                <SqlSourceView className="sessadm-sqltext-body" sql={sqlText.text} loadingText="" />
            )}
        </div>
    ) : null;

    const spanSqls = useMemo(() => {
        const sp = picked2 && picked2.plsqlSpan;
        if (!sp || !picked2.lane) return [];
        const byId = new Map();
        (picked2.lane.segments || []).forEach(seg => {
            if (seg.kind === 'gap' || !seg.sqlId) return;
            if (seg.to <= sp.from || seg.from >= sp.to) return;     // 구간 밖
            const ms = Math.min(seg.to, sp.to) - Math.max(seg.from, sp.from);
            byId.set(seg.sqlId, (byId.get(seg.sqlId) || 0) + Math.max(0, ms));
        });
        return [...byId.entries()]
            .map(([sqlId, ms]) => ({ sqlId, ms }))
            .sort((a, b) => b.ms - a.ms);
    }, [picked2]);

    if (!isOpen) return null;

    // 고른 세션이 끝나 목록에서 빠졌다는 한 줄 — 상세 자리(펼침·접힘 둘 다)에 선다
    const goneNote = pickedGone && (
        <div className="sessadm-gone-note" role="status">
            <FiInfo size={12} />
            <span>{instances.length > 1
                ? t('monitoring:sa.pickedGoneInst', { sid: pickedGone.sid, serial: pickedGone.serial, inst: pickedGone.instId })
                : t('monitoring:sa.pickedGone', { sid: pickedGone.sid, serial: pickedGone.serial })}</span>
            <button type="button" onClick={() => setPickedGone(null)} aria-label={t('monitoring:sa.close')} title={t('monitoring:sa.close')}>
                <FiX size={11} />
            </button>
        </div>
    );


    const queryViewer = queryViewKind && (
        <QueryViewDialog
            title={t('monitoring:sa.viewQueryTitle')}
            sql={typeof queryViewSql === 'string' ? queryViewSql : null}
            error={queryViewSql && queryViewSql.error}
            loadingText={t('monitoring:sa.viewQueryLoading')}
            note={t('monitoring:sa.viewQueryNote')}
            onClose={() => setQueryViewKind(null)}
        />
    );

    return (
        <ConnTabPane active={active} toolbar={<OwnConnTarget hideConn
                conns={mon.conns.filter(c => c.id === mon.connId || !excludeConnIds.includes(c.id))} connId={mon.connId} onConnChange={mon.setConnId}
                sessionId={mon.sessionId} session={mon.session}
                sessionError={mon.sessionError} creating={monCreating}
                onPickSession={mon.pickSession}
            />}>
            {queryViewer}

                <div className="sessadm-toolbar">
                    {/* 연결 선택은 제목줄(OwnConnTarget)로 옮겼다 — 다섯 창이 같은 자리다 */}
                    <select className="sessadm-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        {getStatusOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <div className="sessadm-search">
                        <FiSearch size={12} />
                        <input
                            type="text"
                            placeholder={t('monitoring:sa.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            // Esc 로도 지운다 — 창을 닫는 Esc 보다 먼저 받는다(검색어가 있을 때만)
                            onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); setSearch(''); } }}
                        />
                        {search && (
                            <button type="button" className="sessadm-search-clear" onClick={() => setSearch('')}
                                title={t('monitoring:sa.clearSearch')} aria-label={t('monitoring:sa.clearSearch')}>
                                <FiX size={12} />
                            </button>
                        )}
                    </div>
                    {/* 인스턴스 고르기 — 상태·검색 바로 오른쪽(2026-10-22 요청, 예전에는 표 위 줄 하나를 따로 썼다) */}
                    {instances.length > 1 && (
                        <div className="sessadm-inst-ribbon sessadm-inst-inline">
                            <span className="sessadm-inst-label">{t('monitoring:sa.instance')}</span>
                            <button type="button" className={`sessadm-inst-chip ${activeInst == null ? 'active' : ''}`} onClick={() => setInstFilter(null)}>
                                {t('monitoring:sa.allInstances')}
                            </button>
                            {instances.map(n => (
                                <button key={n} type="button" className={`sessadm-inst-chip ${activeInst === n ? 'active' : ''}`} onClick={() => setInstFilter(n)}>
                                    {n}
                                </button>
                            ))}
                        </div>
                    )}
                    <label className="sessadm-toggle">
                        <input type="checkbox" checked={includeBackground} onChange={(e) => setIncludeBackground(e.target.checked)} />
                        {t('monitoring:sa.includeBackground')}
                    </label>
                    <div className="sessadm-toolbar-spacer" />
                    <button className="sessadm-btn sessadm-btn-secondary" onClick={fetchSessions} disabled={loading}>
                        <FiRefreshCw size={12} className={loading ? 'sessadm-spin' : ''} /> {t('monitoring:sa.refresh')}
                    </button>
                    <label className="sessadm-demo-toggle">
                        <input type="checkbox" checked={useDemo} onChange={(e) => setUseDemo(e.target.checked)} />
                        {t('monitoring:sa.demoData')}
                    </label>
                </div>

                {backendError && (
                    <div className="sessadm-error"><FiInfo size={13} /> {backendError}</div>
                )}
                {killError && (
                    <div className="sessadm-error"><FiAlertTriangle size={13} /> {t('monitoring:sa.killFailed', { message: killError })}</div>
                )}
                {canKill && (
                    <div className="sessadm-note">
                        <FiInfo size={13} /> {t('monitoring:sa.killPrivNote')}
                    </div>
                )}
                {isDemoData && (
                    <div className="sessadm-note">
                        <FiInfo size={13} /> {t('monitoring:sa.demoNote')}
                    </div>
                )}

                <div className="sessadm-body">
                    <table className="sessadm-table">
                        <thead>
                            <tr>
                                {sessCols.map(c => (
                                    <th
                                        key={c.key}
                                        ref={(el) => { thRefs.current[c.key] = el; }}
                                        className={`sessadm-sortable${pinClass(c.key)}`}
                                        style={pinStyle(c.key)}
                                        onClick={() => setSort(prev => nextSort(prev, c.key))}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            setColMenu({ ...menuPoint(e, 200, 120, e.view || window), key: c.key });
                                        }}
                                        title={t('monitoring:sa.headerHint')}
                                        aria-sort={sort && sort.col === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                                    >
                                        {sessColLabel(c.key, t)}
                                        {sort && sort.col === c.key && <span className="sessadm-sort-mark">{sort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                                    </th>
                                ))}
                                {/* 종료 버튼 칸은 오른쪽에 붙어 있다 — 열이 늘어 가로로 스크롤해도 제자리 */}
                                <th className="sessadm-actions-col" />
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSessions.map(s => {
                                const key = sessionRowKey(s);
                                const isKilling = killingKey === key;
                                return (
                                    <tr
                                        key={key}
                                        className={[
                                            s.blockingSession ? 'sessadm-row-blocked' : '',
                                            picked && sessionRowKey(picked) === key ? 'sessadm-row-picked' : '',
                                        ].filter(Boolean).join(' ')}
                                        // 행 어디를 눌러도 그 세션을 고른다(요청 2026-10-02). 셀 값을 검색어로 넣는 것은 **두 번** 누르기라
                                        // 한 번 누르기와 겹치지 않는다. 종료 버튼은 전파를 막는다(눌러도 고르지 않는다)
                                        onClick={() => pickSession(s)}
                                    >
                                        {sessCols.map(c => {
                                            const cls = `${MONO_COLS.has(c.key) ? 'sessadm-mono' : ''}${pinClass(c.key)}`;
                                            if (c.key === 'sid') {
                                                return (
                                                    <td key={c.key} className={`${cls} sessadm-sid-cell`} style={pinStyle(c.key)}>
                                                        <button type="button" className="sessadm-pick" onClick={() => pickSession(s)} title={t('monitoring:sa.pickHint')}>
                                                            {s.sid},{s.serial}
                                                        </button>
                                                        {instances.length > 1 && <span className="sessadm-inst-tag">@{s.instId ?? 1}</span>}
                                                    </td>
                                                );
                                            }
                                            const text = c.text(s);
                                            let body = text || <span className="sessadm-empty">-</span>;
                                            if (c.key === 'status') {
                                                body = <span className={`sessadm-status-badge ${s.status === 'ACTIVE' ? 'active' : 'inactive'}`}>{s.status}</span>;
                                            } else if (c.key === 'elapsed') {
                                                body = fmtElapsed(s.lastCallEt, t);
                                            } else if (c.key === 'blocking' && text && instances.length > 1 && s.blockingInstance != null) {
                                                // RAC — 차단자는 다른 노드에 있을 수 있다(SID 는 노드 안에서만 유일)
                                                body = <>{text}<span className="sessadm-inst-tag">@{s.blockingInstance}</span></>;
                                            }
                                            return (
                                                <td
                                                    key={c.key}
                                                    className={`${cls} ${text ? 'sessadm-searchable' : ''}${search && search === text ? ' sessadm-searching' : ''}`}
                                                    style={pinStyle(c.key)}
                                                    // 한 번 누르기가 아니라 **두 번** — 한 번은 값을 고르거나 읽으려다 검색어가 바뀌었다(요청 2026-10-02).
                                                    // 두 번 누를 때 글자가 선택되며 칸이 파랗게 칠해지는 것은 막는다
                                                    onMouseDown={(e) => { if (e.detail > 1) e.preventDefault(); }}
                                                    onDoubleClick={() => searchByCell(text)}
                                                    title={text ? t('monitoring:sa.cellSearchHint', { value: text }) : undefined}
                                                >
                                                    {body}
                                                </td>
                                            );
                                        })}
                                        <td className="sessadm-actions-col">
                                          <div className="sessadm-actions">
                                            <button
                                                className="sessadm-btn sessadm-btn-danger"
                                                disabled={!canKill || isKilling}
                                                onClick={(e) => { e.stopPropagation(); handleKill(s, 'normal'); }}
                                                title={t('monitoring:sa.mode.normal')}
                                            >
                                                <FiZapOff size={11} /> {t('monitoring:sa.killShort')}
                                            </button>
                                            <button
                                                className="sessadm-btn sessadm-btn-danger"
                                                disabled={!canKill || isKilling}
                                                onClick={(e) => { e.stopPropagation(); handleKill(s, 'immediate'); }}
                                                title={t('monitoring:sa.mode.immediate')}
                                            >
                                                {t('monitoring:sa.killImmediate')}
                                            </button>
                                          </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredSessions.length === 0 && !loading && (
                                <tr><td colSpan={SESSION_COLUMNS.length + 1} className="sessadm-empty-note">{t('monitoring:sa.noMatch')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ── 선택한 세션의 상세 ──────────────────────────────────────
                    목록만으로는 "지금 무엇을 하고 있나" 까지밖에 알 수 없다.
                    조회 대상 뷰별로 탭을 두고, 그 탭이 돌리는 SQL 도 볼 수 있게 한다. */}
                {!detailOpen ? (
                    <div className="sessadm-detail-collapsed">
                        <span className="sessadm-detail-collapsed-title">{t('monitoring:sa.detailTitle')}</span>
                        {picked && (
                            <span className="sessadm-detail-target">
                                {t('monitoring:sa.detailFor', { sid: picked.sid, serial: picked.serial })}
                            </span>
                        )}
                        {!picked && goneNote}
                        <span className="sessadm-detail-collapsed-note">{t('monitoring:sa.detailCollapsedNote')}</span>
                        <div className="sessadm-spacer" />
                        <button type="button" className="sessadm-detail-toggle" onClick={() => setDetailOpen(true)}
                            title={t('monitoring:sa.detailExpand')} aria-expanded={false}>
                            <FiChevronUp size={13} />
                        </button>
                    </div>
                ) : picked ? (
                    <div className="sessadm-detail">
                        <div className="sessadm-detail-tabs">
                            {/* 폭이 고정이라 자릿수가 크면 잘릴 수 있다 — 잘린 SID 를 못 읽으면
                                어느 세션을 보고 있는지 알 수가 없으므로 title 로 남긴다 */}
                            <span
                                className="sessadm-detail-target"
                                title={t('monitoring:sa.detailFor', { sid: picked.sid, serial: picked.serial })}
                            >
                                {t('monitoring:sa.detailFor', { sid: picked.sid, serial: picked.serial })}
                            </span>
                            {DETAIL_TABS(t).map(tab => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`sessadm-detail-tab ${detailTab === tab.key ? 'active' : ''}`}
                                    onClick={() => setDetailTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                            <div className="sessadm-spacer" />
                            {detailTab === 'timeline' && (
                                <label className="sessadm-ash-range" title={canPool(picked) ? t('monitoring:sa.poolHint') : t('monitoring:sa.poolNoKey')}>
                                    <input type="checkbox" checked={poolView} disabled={!canPool(picked)} onChange={(e) => setPoolView(e.target.checked)} />
                                    {t('monitoring:sa.pool')}
                                </label>
                            )}
                            {(detailTab === 'timeline' || detailTab === 'ash') && (
                                <label className="sessadm-ash-range" title={t('monitoring:sa.ashRange')}>
                                    {t('monitoring:sa.ashRange')}
                                    <select value={ashMinutes} onChange={(e) => setAshMinutes(Number(e.target.value))}>
                                        {ASH_MINUTES.map(n => <option key={n} value={n}>{t('monitoring:sa.ashRangeOpt', { n })}</option>)}
                                    </select>
                                </label>
                            )}
                            {detailIsDemo && <span className="sessadm-demo-badge">{t('monitoring:sa.demoData')}</span>}
                            {/* 접기는 새로고침 왼편 — 접힌 줄의 펼치기 버튼과 같은 자리(오른쪽)에 둔다 */}
                            <button type="button" className="sessadm-detail-toggle" onClick={() => setDetailOpen(false)}
                                title={t('monitoring:sa.detailCollapse')} aria-expanded>
                                <FiChevronDown size={13} />
                            </button>
                            <button
                                className="sessadm-btn sessadm-btn-secondary"
                                onClick={() => fetchDetail(detailTab, picked)}
                                disabled={detailLoading === detailTab}
                                title={t('monitoring:sa.refresh')}
                            >
                                <FiRefreshCw size={11} className={detailLoading === detailTab ? 'sessadm-spin' : ''} />
                            </button>
                            <QueryViewButton onClick={() => setQueryViewKind(detailTab === 'timeline' ? 'ash' : detailTab)} title={t('monitoring:sa.viewQueryNote')} />
                        </div>

                        {detailError && (
                            <div className="sessadm-error"><FiInfo size={13} /> {detailError}</div>
                        )}

                        <div className="sessadm-detail-body">
                            {/* 불러오는 동안 빈 머리줄만 그리지 않는다 — 그것이 깜빡임으로 보였다 */}
                            {!showDetailBody && (
                                <div className="sessadm-empty-note">{t('monitoring:sa.detailLoading')}</div>
                            )}
                            {showDetailBody && detailTab === 'timeline' && (
                                <>
                                    {qcOfPicked && (
                                        <div className="sessadm-qc-hint">
                                            <FiInfo size={12} />
                                            <span>{t('monitoring:sa.tl.workerOf', { sid: qcOfPicked.sid, serial: qcOfPicked.serial ?? '?' })}</span>
                                            <button type="button" className="sessadm-btn sessadm-btn-secondary" onClick={goToQc}>
                                                {t('monitoring:sa.tl.viewQc')}
                                            </button>
                                        </div>
                                    )}
                                    <AshTimeline
                                        samples={detail.timeline || []}
                                        width={980}
                                        onSelect={setPicked2}
                                        isDarkMode={darkMode}
                                        resetKey={pickedKey}
                                        sqlArea={sqlArea}
                                        onOpenSqlText={openSqlText}
                                        onOpenHistory={mon.connId && !useDemo ? goHistory : undefined}
                                        pool={!!ashMeta.pool}
                                        commitStats={ashMeta.commitStats || null}
                                        truncated={!!ashMeta.truncated}
                                    />
                                    {sqlTextPanel}
                                    {picked2 && picked2.segment && (
                                        <div className="sessadm-seg-detail">
                                            <b>{picked2.segment.kind === 'cpu' ? t('monitoring:sa.tl.onCpu')
                                                : picked2.segment.kind === 'commit' ? `${t('monitoring:sa.tc.commit')}${picked2.segment.event ? ` · ${picked2.segment.event}` : ''}`
                                                : picked2.segment.event}</b>
                                            {picked2.segment.sqlId && <code>{picked2.segment.sqlId}</code>}
                                            {picked2.segment.sqlExecId != null && <span>EXEC {picked2.segment.sqlExecId}</span>}
                                            <span>{t('monitoring:sa.tl.samples', { n: picked2.segment.samples })}</span>
                                            {picked2.segment.waitClass && <span>{picked2.segment.waitClass}</span>}
                                            {picked2.segment.plsqlName && <span className="sessadm-plsql-tag">{picked2.segment.plsqlName}</span>}
                                            <button type="button" onClick={() => setPicked2(null)} aria-label={t('monitoring:sa.close')}>
                                                <FiX size={11} />
                                            </button>
                                        </div>
                                    )}

                                    {/* PL/SQL 구간을 누르면 **그 안에서 돈 SQL** 을 펼친다.
                                        표본 시간(이 호출 안) 과 전역 누적(GV$SQLAREA)을 나란히 둔다 —
                                        둘은 다른 값이고, 섞으면 "이 호출이 4,210번 돌았다" 로 오해한다. */}
                                    {picked2 && picked2.plsqlSpan && (
                                        <div className="sessadm-plsql-detail">
                                            <div className="sessadm-plsql-head">
                                                <b>{picked2.plsqlSpan.name || picked2.plsqlSpan.id}</b>
                                                <span>{t('monitoring:sa.tl.spanDur', { sec: Math.round((picked2.plsqlSpan.to - picked2.plsqlSpan.from) / 100) / 10 })}</span>
                                                <div className="sessadm-spacer" />
                                                <button type="button" onClick={() => setPicked2(null)} aria-label={t('monitoring:sa.close')}>
                                                    <FiX size={11} />
                                                </button>
                                            </div>
                                            <table className="sessadm-table sessadm-table-compact">
                                                <thead>
                                                    <tr>
                                                        <th>SQL_ID</th><th>SQL</th>
                                                        <th title={t('monitoring:sa.tl.sampledHint')}>{t('monitoring:sa.tl.sampled')}</th>
                                                        <th title={t('monitoring:sa.tl.globalHint')}>{t('monitoring:sa.tl.execCount')}</th>
                                                        <th title={t('monitoring:sa.tl.globalHint')}>{t('monitoring:sa.tl.avgPerExec')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {spanSqls.map(row => {
                                                        const a = sqlArea[row.sqlId];
                                                        const avg = (a && a.executions > 0) ? a.elapsedSec / a.executions : null;
                                                        return (
                                                            <tr key={row.sqlId}>
                                                                <td className="sessadm-mono">{row.sqlId}</td>
                                                                <td className="sessadm-sqlcell" title={a?.sqlHead || ''}>{a?.sqlHead || '-'}</td>
                                                                <td className="sessadm-num">{Math.round(row.ms / 100) / 10}s</td>
                                                                {/* 없으면 0 으로 채우지 않는다 — 공유 풀에서 밀려나면 행이 아예 없다 */}
                                                                <td className="sessadm-num">{a?.executions?.toLocaleString?.() ?? '-'}</td>
                                                                <td className="sessadm-num">{avg != null ? `${Math.round(avg * 1000)}ms` : '-'}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {spanSqls.length === 0 && (
                                                        <tr><td colSpan={5} className="sessadm-empty-note">{t('monitoring:sa.tl.noSqlInSpan')}</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                            <div className="sessadm-plsql-note">
                                                <FiInfo size={12} /> {t('monitoring:sa.tl.plsqlNote')}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {showDetailBody && detailTab === 'cursors' && (
                                <table className="sessadm-table sessadm-table-compact">
                                    <thead>
                                        <tr><th>{t('monitoring:sa.cur.col')}</th><th>SQL_ID</th><th>{t('monitoring:sa.col.cursorType')}</th><th>SQL</th><th>{t('monitoring:sa.col.execCount')}</th><th>{t('monitoring:sa.col.elapsed')}</th><th>Buffer gets</th><th>Disk reads</th></tr>
                                    </thead>
                                    <tbody>
                                        {(detail.cursors || []).map((c, i) => (
                                            <tr key={`${c.sqlId}-${i}`}
                                                className={`sessadm-cursor-row${c.curFlag === 'CURRENT' ? ' is-current' : ''}${sqlText && sqlText.sqlId === c.sqlId ? ' is-open' : ''}`}
                                                title={t('monitoring:sa.cur.hint')}
                                                onClick={() => openSqlText(c.sqlId)}
                                                onDoubleClick={() => { if (mon.connId && !useDemo) goHistory(c.sqlId); }}>
                                                <td>
                                                    {c.curFlag === 'CURRENT' && <span className="sessadm-cur-badge current" title={t('monitoring:sa.cur.currentHint')}>{t('monitoring:sa.cur.current')}</span>}
                                                    {c.curFlag === 'PREV' && <span className="sessadm-cur-badge prev" title={t('monitoring:sa.cur.prevHint')}>{t('monitoring:sa.cur.prev')}</span>}
                                                </td>
                                                <td className="sessadm-mono">{c.sqlId || '-'}</td>
                                                <td>{c.cursorType || '-'}</td>
                                                <td className="sessadm-sqlcell" title={c.sqlHead || ''}>{c.sqlHead || '-'}</td>
                                                <td>{c.executions ?? '-'}</td>
                                                <td>{c.elapsedSec != null ? `${c.elapsedSec}s` : '-'}</td>
                                                <td>{c.bufferGets?.toLocaleString?.() ?? '-'}</td>
                                                <td>{c.diskReads?.toLocaleString?.() ?? '-'}</td>
                                            </tr>
                                        ))}
                                        {(detail.cursors || []).length === 0 && detailLoading !== 'cursors' && (
                                            <tr><td colSpan={8} className="sessadm-empty-note">{t('monitoring:sa.noCursors')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                            {showDetailBody && detailTab === 'cursors' && sqlTextPanel}

                            {showDetailBody && detailTab === 'transaction' && (
                                (detail.transaction || []).length === 0 && detailLoading !== 'transaction' ? (
                                    /* 트랜잭션이 없는 것은 오류가 아니다 — 열린 트랜잭션이 없을 뿐이다 */
                                    <div className="sessadm-empty-note">{t('monitoring:sa.noTransaction')}</div>
                                ) : (
                                    <table className="sessadm-table sessadm-table-compact">
                                        <thead>
                                            <tr><th>XID</th><th>Status</th><th>{t('monitoring:sa.col.startTime')}</th><th>UNDO blocks</th><th>UNDO recs</th><th>Log I/O</th><th>Phys I/O</th></tr>
                                        </thead>
                                        <tbody>
                                            {(detail.transaction || []).map((x, i) => (
                                                <tr key={`${x.xid}-${i}`}>
                                                    <td className="sessadm-mono">{x.xid}</td>
                                                    <td>{x.status}</td>
                                                    <td className="sessadm-mono">{x.startTime || '-'}</td>
                                                    <td>{x.usedUblk?.toLocaleString?.() ?? '-'}</td>
                                                    <td>{x.usedUrec?.toLocaleString?.() ?? '-'}</td>
                                                    <td>{x.logIo?.toLocaleString?.() ?? '-'}</td>
                                                    <td>{x.phyIo?.toLocaleString?.() ?? '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}

                            {showDetailBody && detailTab === 'stats' && (
                                <table className="sessadm-table sessadm-table-compact">
                                    <thead>
                                        <tr><th>{t('monitoring:sa.col.statName')}</th><th>{t('monitoring:sa.col.statValue')}</th></tr>
                                    </thead>
                                    <tbody>
                                        {(detail.stats || []).map((st, i) => (
                                            <tr key={`${st.name}-${i}`}>
                                                <td>{st.name}</td>
                                                <td className="sessadm-num">{st.value?.toLocaleString?.() ?? st.value}</td>
                                            </tr>
                                        ))}
                                        {(detail.stats || []).length === 0 && detailLoading !== 'stats' && (
                                            <tr><td colSpan={2} className="sessadm-empty-note">{t('monitoring:sa.noStats')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {showDetailBody && detailTab === 'ash' && (
                                <table className="sessadm-table sessadm-table-compact">
                                    <thead>
                                        <tr><th>{t('monitoring:sa.col.sampleTime')}</th><th>SID</th><th>SQL_ID</th><th>EXEC</th><th>{t('monitoring:sa.col.waitEvent')}</th><th>Wait class</th><th>PX</th></tr>
                                    </thead>
                                    <tbody>
                                        {(detail.ash || []).slice(0, 300).map((a, i) => (
                                            <tr key={i}>
                                                <td className="sessadm-mono">{(a.sampleTime || '').replace('T', ' ')}</td>
                                                <td>{a.sessionId}</td>
                                                <td className="sessadm-mono">{a.sqlId || '-'}</td>
                                                <td>{a.sqlExecId ?? '-'}</td>
                                                {/* event 가 비면 ON CPU 다 — 빈 칸으로 두면 "모른다" 로 읽힌다 */}
                                                <td className="sessadm-mono">{a.event || t('monitoring:sa.tl.onCpu')}</td>
                                                <td>{a.waitClass || '-'}</td>
                                                <td>{a.pxServerNumber ?? '-'}</td>
                                            </tr>
                                        ))}
                                        {(detail.ash || []).length === 0 && detailLoading !== 'ash' && (
                                            <tr><td colSpan={7} className="sessadm-empty-note">{t('monitoring:sa.tl.noSamples')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="sessadm-detail sessadm-detail-hint">
                        <button type="button" className="sessadm-detail-toggle sessadm-detail-toggle-corner" onClick={() => setDetailOpen(false)}
                            title={t('monitoring:sa.detailCollapse')} aria-expanded>
                            <FiChevronDown size={13} />
                        </button>
                        {goneNote}
                        {t('monitoring:sa.pickSession')}
                    </div>
                )}

                <div className="sessadm-footer">
                    <div className="sessadm-footer-note">
                        <FiInfo size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {t('monitoring:sa.shownCount', { n: filteredSessions.length })}
                    </div>
                    <button className="app-btn sessadm-btn sessadm-btn-secondary" onClick={onClose}>{t('monitoring:sa.close')}</button>
                </div>
            {colMenu && (
                <div ref={colMenuRef} className="rg-header-menu" style={{ left: colMenu.x, top: colMenu.y }} role="menu">
                    <button type="button" role="menuitem" onClick={() => {
                        const k = colMenu.key;
                        setPinnedCols(prev => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]));
                        setColMenu(null);
                    }}>
                        {pinnedCols.includes(colMenu.key) ? t('common:rg.unpinCol') : t('common:rg.pinCol')}
                    </button>
                    <button type="button" role="menuitem" disabled={pinnedCols.length === 0} onClick={() => { setPinnedCols([]); setColMenu(null); }}>
                        {t('common:rg.unpinAllCols')}
                    </button>
                </div>
            )}
        </ConnTabPane>
    );
};


/**
 * 창 — **DB 연결마다 탭**(2026-10-14 요청: 여러 DB 를 동시에 조회). 탭마다 위의 본문 하나(자기 연결의 전담 세션).
 */
const SessionAdminModal = (props) => {
    const { t } = useTranslation();
    return (
    <ConnTabsWindow sessionLabel="session-admin"
        isOpen={props.isOpen} onClose={props.onClose} connections={props.connections}
        defaultConnectionId={props.defaultConnectionId} filter={ORACLE_CONN}
        icon={<FiUserX size={14} />} title={t('monitoring:sa.title')} overlayClassName="sessadm-overlay" className="sessadm-modal" headerClassName="sessadm-header" closeClassName="sessadm-close"
        renderBody={(tab) => <SessionAdminModalBody {...props} {...tab} />}
    />
    );
};

// 작업 탭을 오갈 때 Header 가 새 콜백을 넘겨도 본문을 다시 그리지 않는다(common/memoWindow)
export default memoWindow(SessionAdminModal);
